import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import m4th from "m4th";
import { Helmet } from "react-helmet";
import { Stack, Switch, Typography } from "@mui/material";
import Latex from "react-latex-next";

const M = m4th.matrix;
const stepRatioinIteration = 60;
const stepPenddingRatioIteration = 30;

var canvasClear = function (ctx, width, height) {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.restore();
};

var drawLine = function (ctx, startx, starty, endx, endy) {
  ctx.beginPath();
  ctx.moveTo(startx, starty);
  ctx.lineTo(endx, endy);
  ctx.stroke();
};

function canvas_arrow(context, fromx, fromy, tox, toy, color) {
  context.beginPath();
  context.strokeStyle = color;
  var headlen = 10; // length of head in pixels
  var dx = tox - fromx;
  var dy = toy - fromy;
  var angle = Math.atan2(dy, dx);
  context.moveTo(fromx, fromy);
  context.lineTo(tox, toy);
  context.lineTo(
    tox - headlen * Math.cos(angle - Math.PI / 6),
    toy - headlen * Math.sin(angle - Math.PI / 6)
  );
  context.moveTo(tox, toy);
  context.lineTo(
    tox - headlen * Math.cos(angle + Math.PI / 6),
    toy - headlen * Math.sin(angle + Math.PI / 6)
  );
  context.stroke();
}

let previousTimeStamp = 0;

function Main() {
  const [l1, setL1] = useState(60);
  const [l2, setL2] = useState(60);
  const [l3, setL3] = useState(60);
  const [l4, setL4] = useState(60);
  const [xPos, setXPos] = useState(520);
  const [yPos, setYPos] = useState(300);
  const [angCurrent, setAngCurrent] = useState<number[]>([0, 0, 1, 0]);
  const [alpha, setAlpha] = useState(0.3);
  const [epsilon, setEpsilon] = useState(10);
  const [run, setRun] = useState(true);
  const [showJacobian, setShowJacobian] = useState(false);
  const [showIteration, setShowIteration] = useState(false);
  const [iteration, setIteration] = useState(0);
  const [step, setStep] = useState(0);
  const [iterationdQ, setIterationdQ] = useState(
    M(4, [angCurrent[0], angCurrent[1], angCurrent[2], angCurrent[3]])
  );
  const [iterationJ, setIterationJ] = useState(M(2, [0, 0, 0, 0, 0, 0, 0, 0]));
  const [iterationdX, setIterationdX] = useState(M(2, [0, 0]));
  const [iterationAng, setIterationAng] = useState<number[]>([0, 0, 1, 0]);
  const [iterationInvJ, setIterationInvJ] = useState(
    M(4, [0, 0, 0, 0, 0, 0, 0, 0])
  );

  const canvas = useRef<HTMLCanvasElement>();

  useEffect(() => {
    let requestId;
    const canvasEle = canvas.current;
    const ctx = canvasEle.getContext("2d");
    canvasEle.addEventListener("click", getClickPosition);

    function getClickPosition(e) {
      var parentPosition = getPosition(e.currentTarget);
      setXPos(e.clientX - parentPosition.x);
      setYPos(
        e.clientY + document.documentElement.scrollTop - parentPosition.y
      );
      setIteration(0);
      setStep(0);
    }

    function getPosition(element) {
      var xPosition = 0;
      var yPosition = 0;

      while (element) {
        xPosition +=
          element.offsetLeft - element.scrollLeft + element.clientLeft;
        yPosition += element.offsetTop - element.scrollTop + element.clientTop;
        element = element.offsetParent;
      }
      return { x: xPosition, y: yPosition };
    }
    const render = (w) => {
      const timeStamp = Date.now();
      const elapsed = timeStamp - previousTimeStamp;
      if (elapsed > 30 && run) {
        //target position
        var Pgoal = M(2, [
          xPos, //x
          yPos, //y
        ]);

        //describes DoF variables at current state
        var l = [l1, l2, l3, l4]; //length of joint

        var Q = M(4, [
          angCurrent[0],
          angCurrent[1],
          angCurrent[2],
          angCurrent[3],
        ]);

        //forward kinematic equations, generalized for i amount of 2d revolute joints with orthogonal axis of rotation
        var Px = 400;
        var Py = 300;
        for (var i = 0; i < Q.rows; i++) {
          var angSum = 0;
          for (var k = 0; k < i + 1; k++) angSum += Q.get(k, 0);
          Px += l[i] * Math.cos(angSum);
          Py += l[i] * Math.sin(angSum);
        }

        var P = M(2, [Px, Py]);

        // // //clears the canvas
        canvasClear(ctx, canvasEle.width, canvasEle.height);
        //draws the origin
        ctx.strokeStyle = "#757575";
        ctx.fillStyle = "black";
        ctx.beginPath();
        ctx.arc(400, 300, 5, 0, 2 * Math.PI, false);
        ctx.fill();

        //draws the the circle that bounds the arm
        ctx.strokeStyle = "#757575";
        ctx.fillStyle = "white";
        ctx.beginPath();
        ctx.arc(400, 300, l[0] + l[1] + l[2] + l[3], 0, 2 * Math.PI, false);
        ctx.lineWidth = 5;
        ctx.stroke();
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.fillStyle = "red";
        var dX = Pgoal.minus(P);

        //draw goal point
        ctx.beginPath();
        ctx.arc(Pgoal.get(0, 0), Pgoal.get(1, 0), 1, 0, 2 * Math.PI, false);
        ctx.fill();

        //draw epsilon circle
        ctx.beginPath();
        ctx.arc(
          Pgoal.get(0, 0),
          Pgoal.get(1, 0),
          epsilon,
          0,
          2 * Math.PI,
          false
        );
        ctx.stroke();

        let dQ = M(4, [0, 0, 0, 0]);
        const goalDistance =
          dX.get(0, 0) * dX.get(0, 0) + dX.get(1, 0) * dX.get(1, 0);

        const d1x =
          -l[0] * Math.sin(Q.get(0, 0)) -
          l[1] * Math.sin(Q.get(0, 0)) * Math.cos(Q.get(1, 0)) -
          l[1] * Math.cos(Q.get(0, 0)) * Math.sin(Q.get(1, 0)) -
          l[2] * Math.sin(Q.get(0, 0)) * Math.cos(Q.get(1, 0) + Q.get(2, 0)) -
          l[2] * Math.cos(Q.get(0, 0)) * Math.sin(Q.get(1, 0) + Q.get(2, 0)) -
          l[3] *
            Math.sin(Q.get(0, 0)) *
            Math.cos(Q.get(1, 0) + Q.get(2, 0) + Q.get(3, 0)) -
          l[3] *
            Math.cos(Q.get(0, 0)) *
            Math.sin(Q.get(1, 0) + Q.get(2, 0) + Q.get(3, 0));
        const d1y =
          l[0] * Math.cos(Q.get(0, 0)) +
          l[1] * Math.cos(Q.get(0, 0)) * Math.cos(Q.get(1, 0)) -
          l[1] * Math.sin(Q.get(0, 0)) * Math.sin(Q.get(1, 0)) +
          l[2] * Math.cos(Q.get(0, 0)) * Math.cos(Q.get(1, 0) + Q.get(2, 0)) -
          l[2] * Math.sin(Q.get(0, 0)) * Math.sin(Q.get(1, 0) + Q.get(2, 0)) +
          l[3] *
            Math.cos(Q.get(0, 0)) *
            Math.cos(Q.get(1, 0) + Q.get(2, 0) + Q.get(3, 0)) -
          l[3] *
            Math.sin(Q.get(0, 0)) *
            Math.sin(Q.get(1, 0) + Q.get(2, 0) + Q.get(3, 0));

        const d2x =
          -l[1] * Math.sin(Q.get(1, 0)) * Math.cos(Q.get(0, 0)) -
          l[1] * Math.cos(Q.get(1, 0)) * Math.sin(Q.get(0, 0)) -
          l[2] * Math.sin(Q.get(1, 0)) * Math.cos(Q.get(0, 0) + Q.get(2, 0)) -
          l[2] * Math.cos(Q.get(1, 0)) * Math.sin(Q.get(0, 0) + Q.get(2, 0)) -
          l[3] *
            Math.sin(Q.get(1, 0)) *
            Math.cos(Q.get(0, 0) + Q.get(2, 0) + Q.get(3, 0)) -
          l[3] *
            Math.cos(Q.get(1, 0)) *
            Math.sin(Q.get(0, 0) + Q.get(2, 0) + Q.get(3, 0));
        const d2y =
          l[1] * Math.cos(Q.get(1, 0)) * Math.cos(Q.get(0, 0)) -
          l[1] * Math.sin(Q.get(1, 0)) * Math.sin(Q.get(0, 0)) +
          l[2] * Math.cos(Q.get(1, 0)) * Math.cos(Q.get(0, 0) + Q.get(2, 0)) -
          l[2] * Math.sin(Q.get(1, 0)) * Math.sin(Q.get(0, 0) + Q.get(2, 0)) +
          l[3] *
            Math.cos(Q.get(1, 0)) *
            Math.cos(Q.get(0, 0) + Q.get(2, 0) + Q.get(3, 0)) -
          l[3] *
            Math.sin(Q.get(1, 0)) *
            Math.sin(Q.get(0, 0) + Q.get(2, 0) + Q.get(3, 0));

        const d3x =
          -l[2] * Math.sin(Q.get(2, 0)) * Math.cos(Q.get(0, 0) + Q.get(1, 0)) -
          l[2] * Math.cos(Q.get(2, 0)) * Math.sin(Q.get(0, 0) + Q.get(1, 0)) -
          l[3] *
            Math.sin(Q.get(2, 0)) *
            Math.cos(Q.get(0, 0) + Q.get(1, 0) + Q.get(3, 0)) -
          l[3] *
            Math.cos(Q.get(2, 0)) *
            Math.sin(Q.get(0, 0) + Q.get(1, 0) + Q.get(3, 0));
        const d3y =
          l[2] * Math.cos(Q.get(2, 0)) * Math.cos(Q.get(0, 0) + Q.get(1, 0)) -
          l[2] * Math.sin(Q.get(2, 0)) * Math.sin(Q.get(0, 0) + Q.get(1, 0)) +
          l[3] *
            Math.cos(Q.get(2, 0)) *
            Math.cos(Q.get(0, 0) + Q.get(1, 0) + Q.get(3, 0)) -
          l[3] *
            Math.sin(Q.get(2, 0)) *
            Math.sin(Q.get(0, 0) + Q.get(1, 0) + Q.get(3, 0));

        const d4x =
          -l[3] *
            Math.sin(Q.get(3, 0)) *
            Math.cos(Q.get(0, 0) + Q.get(1, 0) + Q.get(2, 0)) -
          l[3] *
            Math.cos(Q.get(3, 0)) *
            Math.sin(Q.get(0, 0) + Q.get(1, 0) + Q.get(2, 0));
        const d4y =
          l[3] *
            Math.cos(Q.get(3, 0)) *
            Math.cos(Q.get(0, 0) + Q.get(1, 0) + Q.get(2, 0)) -
          l[3] *
            Math.sin(Q.get(3, 0)) *
            Math.sin(Q.get(0, 0) + Q.get(1, 0) + Q.get(2, 0));

        const J = M(2, [d1x, d2x, d3x, d4x, d1y, d2y, d3y, d4y]);
        if (showJacobian) {
          canvas_arrow(ctx, Px, Py, Px + d1x, Py + d1y, "green");
          canvas_arrow(ctx, Px, Py, Px + d2x, Py + d2y, "blue");
          canvas_arrow(ctx, Px, Py, Px + d3x, Py + d3y, "orange");
          canvas_arrow(ctx, Px, Py, Px + d4x, Py + d4y, "red");
          canvas_arrow(
            ctx,
            Px,
            Py,
            Px + (d1x + d2x + d3x + d4x),
            Py + (d1y + d2y + d3y + d4y),
            "Fuchsia"
          );
        }

        if (goalDistance > epsilon * epsilon) {
          var jInv;
          //LU factorization and lin least squares inversion  jT(jjT)*     (jTj)*jT
          if (J.columns > J.rows)
            jInv = J.transp().mult(m4th.lu(J.mult(J.transp())).getInverse());
          else if (J.columns < J.rows)
            jInv = m4th.lu(J.transp().mult(J)).getInverse().mult(J.transp());
          else jInv = m4th.lu(J).getInverse();

          dQ = jInv.mult(dX);
        }

        if (showIteration) {
          if (step == 0 && goalDistance > epsilon * epsilon) {
            setIterationdQ(dQ.times(alpha));
            setIterationdX(dX);
            setIterationJ(J);
            setIterationInvJ(jInv);
            const tempIterAng = [0, 0, 0, 0];
            for (var i = 0; i < Q.rows; i++) {
              tempIterAng[i] = Q.get(i, 0);
            }
            setIterationAng(tempIterAng);
            setIteration(iteration + 1);
          }
          //update Q one step
          if (step == 0 && goalDistance < epsilon * epsilon) {
          } else {
            if (step < stepRatioinIteration)
              Q = Q.add(iterationdQ.times(1 / stepRatioinIteration));
            setStep(
              step == stepRatioinIteration + stepPenddingRatioIteration - 1
                ? 0
                : step + 1
            );
          }
        } else {
          //update Q for each iteration
          Q = Q.add(dQ.times(alpha));
        }

        Px = 0;
        Py = 0;

        // //updates Q for drawing
        for (var i = 0; i < Q.rows; i++) {
          angSum = 0;
          for (var j = 0; j < i + 1; j++) angSum += Q.get(j, 0);
          Px += l[i] * Math.cos(angSum);
          Py += l[i] * Math.sin(angSum);
        }

        // //updates the array Q is set from
        const tempAng = [0, 0, 0, 0];
        for (var i = 0; i < Q.rows; i++) {
          tempAng[i] = Q.get(i, 0);
        }
        setAngCurrent(tempAng);

        // //really verbose way of drawing the links
        ctx.beginPath();
        ctx.arc(Pgoal.get(0, 0), Pgoal.get(1, 0), 5, 0, 2 * Math.PI, false);
        ctx.fill();

        ctx.strokeStyle = "black";
        drawLine(
          ctx,
          400,
          300,
          400 + l[0] * Math.cos(Q.get(0, 0)),
          300 + l[0] * Math.sin(Q.get(0, 0))
        );

        ctx.strokeStyle = "black";
        ctx.fillStyle = "black";
        ctx.beginPath();
        ctx.arc(
          400 + l[0] * Math.cos(Q.get(0, 0)),
          300 + l[0] * Math.sin(Q.get(0, 0)),
          5,
          0,
          2 * Math.PI,
          false
        );
        ctx.fill();

        ctx.save();

        ctx.translate(
          400 + l[0] * Math.cos(Q.get(0, 0)),
          300 + l[0] * Math.sin(Q.get(0, 0))
        );
        ctx.rotate(Q.get(0, 0));

        drawLine(
          ctx,
          0,
          0,
          l[1] * Math.cos(Q.get(1, 0)),
          l[1] * Math.sin(Q.get(1, 0))
        );
        ctx.beginPath();
        ctx.arc(
          l[1] * Math.cos(Q.get(1, 0)),
          l[1] * Math.sin(Q.get(1, 0)),
          5,
          0,
          2 * Math.PI,
          false
        );
        ctx.fill();
        ctx.translate(
          l[1] * Math.cos(Q.get(1, 0)),
          l[1] * Math.sin(Q.get(1, 0))
        );
        ctx.rotate(Q.get(1, 0));

        drawLine(
          ctx,
          0,
          0,
          l[2] * Math.cos(Q.get(2, 0)),
          l[2] * Math.sin(Q.get(2, 0))
        );
        ctx.beginPath();
        ctx.arc(
          l[2] * Math.cos(Q.get(2, 0)),
          l[2] * Math.sin(Q.get(2, 0)),
          5,
          0,
          2 * Math.PI,
          false
        );
        ctx.fill();
        ctx.translate(
          l[2] * Math.cos(Q.get(2, 0)),
          l[2] * Math.sin(Q.get(2, 0))
        );
        ctx.rotate(Q.get(2, 0));

        drawLine(
          ctx,
          0,
          0,
          l[3] * Math.cos(Q.get(3, 0)),
          l[3] * Math.sin(Q.get(3, 0))
        );
        ctx.beginPath();
        ctx.arc(
          l[3] * Math.cos(Q.get(3, 0)),
          l[3] * Math.sin(Q.get(3, 0)),
          5,
          0,
          2 * Math.PI,
          false
        );
        ctx.fill();

        ctx.restore();
        previousTimeStamp = timeStamp;
      }
      requestId = requestAnimationFrame(() => render(w + 1));
    };

    render(0);

    return () => {
      cancelAnimationFrame(requestId);
    };
  }, [
    l1,
    l2,
    l3,
    l4,
    xPos,
    yPos,
    angCurrent,
    alpha,
    epsilon,
    showJacobian,
    run,
    step,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 7 }}>
        <Controls
          controls={{
            l1,
            l2,
            l3,
            l4,
            setL1,
            setL2,
            setL3,
            setL4,
            alpha,
            setAlpha,
            epsilon,
            setEpsilon,
            showJacobian,
            setShowJacobian,
            showIteration,
            setShowIteration,
            setRun,
            setIteration,
            setStep,
          }}
        />
      </Box>
      <Box
        sx={{
          flex: 13,
          backgroundColor: "#eeeeee",
          position: "relative",
        }}
      >
        {showIteration && (
          <Box
            sx={{
              position: "absolute",
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              margin: 2,
              maxWidth: 200,
            }}
          >
            <Typography>Iteration: {iteration}</Typography>
            <Typography>
              <Latex>$\Delta \theta_1$</Latex>:{" "}
              {-((iterationdQ.get(0, 0) * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\Delta \theta_2$</Latex>:{" "}
              {-((iterationdQ.get(1, 0) * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\Delta \theta_3$</Latex>:{" "}
              {-((iterationdQ.get(2, 0) * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\Delta \theta_4$</Latex>:{" "}
              {-((iterationdQ.get(3, 0) * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography sx={{ marginTop: 1 }}>Current Angles:</Typography>
            <Typography>
              <Latex>$\theta_1$</Latex>:{" "}
              {-((angCurrent[0] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\theta_2$</Latex>:{" "}
              {-((angCurrent[1] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\theta_3$</Latex>:{" "}
              {-((angCurrent[2] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\theta_4$</Latex>:{" "}
              {-((angCurrent[3] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography variant="caption">Unit of Angle is Degree</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>Pause</Typography>
              <Switch
                checked={run}
                onClick={() => setRun(!run)}
                inputProps={{ "aria-label": "ant design" }}
              />
              <Typography>Run</Typography>
            </Stack>

            <Typography sx={{ marginTop: 1, textAlign: "left" }}>
              The initial parameters <br />
              in this iteration {iteration}:
            </Typography>
            <Typography>
              <Latex>$\theta_1$</Latex>:{" "}
              {-((iterationAng[0] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\theta_2$</Latex>:{" "}
              {-((iterationAng[1] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\theta_3$</Latex>:{" "}
              {-((iterationAng[2] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\theta_4$</Latex>:{" "}
              {-((iterationAng[3] * 180) / Math.PI).toFixed(2)}
            </Typography>
            <Typography variant="caption">
              <Latex>
                {` $$J : \\left[ \\begin{array}
          {cccc} ${-iterationJ.get(0, 0).toFixed(1)}& ${-iterationJ
                  .get(0, 1)
                  .toFixed(1)} & ${-iterationJ
                  .get(0, 2)
                  .toFixed(1)} & ${-iterationJ
                  .get(0, 3)
                  .toFixed(1)} \\\\ ${iterationJ
                  .get(1, 0)
                  .toFixed(1)} & ${iterationJ
                  .get(1, 1)
                  .toFixed(1)} & ${iterationJ
                  .get(1, 2)
                  .toFixed(1)} & ${iterationJ
                  .get(1, 3)
                  .toFixed(1)} \\\\ \\end{array}
          \\right]$$`}
              </Latex>
            </Typography>
            <Typography variant="caption">
              <Latex>
                {` $$J^{+} : \\left[ \\begin{array}
          {cc} ${-iterationInvJ.get(0, 0).toFixed(5)}& ${iterationInvJ
                  .get(0, 1)
                  .toFixed(5)} \\\\ ${-iterationInvJ
                  .get(1, 0)
                  .toFixed(5)} & ${iterationInvJ
                  .get(1, 1)
                  .toFixed(5)} \\\\ ${-iterationInvJ
                  .get(2, 0)
                  .toFixed(5)} & ${iterationInvJ
                  .get(2, 1)
                  .toFixed(5)} \\\\ ${-iterationInvJ
                  .get(3, 0)
                  .toFixed(5)} & ${iterationInvJ
                  .get(3, 1)
                  .toFixed(5)} \\\\ \\end{array}
          \\right]$$`}
              </Latex>
            </Typography>
            <Typography>
              <Latex>$\Delta x$</Latex>: {iterationdX.get(0, 0).toFixed(2)}
            </Typography>
            <Typography>
              <Latex>$\Delta y$</Latex>: {-iterationdX.get(1, 0).toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ textAlign: "left" }}>
              If you try to multiple{" "}
              <Latex>
                $\alpha, J^+, \Delta x$ and $\Delta y$ here, be careful the unit
                of the result is radian.
              </Latex>
            </Typography>
          </Box>
        )}
        <canvas ref={canvas} width="800" height="800"></canvas>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function Jacobian2DIK(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <>
      <Main />
      <Helmet>
        <title>Visualization of 2D Jacobian Inverse Kinematics</title>
        <meta
          name="description"
          content="Interactive 2D Jacobian Inverse Kinematics: Click to move a 4-link planar arm to any position. Adjust link lengths and visualize the Jacobian for each angle. Features slow animation mode to display iteration steps, angle changes, and Jacobian values. Ideal for learning and visualizing inverse kinematics."
        />
      </Helmet>
    </>
  );
}

export default Jacobian2DIK;
